import * as THREE from 'three';
import { isoBands } from 'marchingsquares';
import zapMp3Url from './assets/sfx/zap.mp3';
import logoUrl   from './assets/anti-ultra-red.png';

/* ─────────────────────────────────────────────────────────────
   Scene, textures
   ───────────────────────────────────────────────────────────── */
const canvas = document.querySelector('canvas.webgl');
const scene = new THREE.Scene();

const textureLoader = new THREE.TextureLoader();
const particleTexture1 = textureLoader.load('/textures/particles/red_no_trans.png');
particleTexture1.magFilter = THREE.NearestFilter;
particleTexture1.minFilter = THREE.NearestFilter;
const particleTexture2 = textureLoader.load('/textures/particles/red_x_trans.png');
particleTexture2.magFilter = THREE.NearestFilter;
particleTexture2.minFilter = THREE.NearestFilter;

/* ─────────────────────────────────────────────────────────────
   PARTICLES — parallax-tilt "straw" field (unchanged from prev)
   ───────────────────────────────────────────────────────────── */
const COUNT   = 260;
const FIELD_R = 8.5;
const MAX_TILT_DEG = 9;
const MAX_TILT = MAX_TILT_DEG * Math.PI / 180;
const TILT_LERP = 0.055;

function makeSpherePositions(count, radius, minR = 0.15) {
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
        const theta = Math.random() * 2 * Math.PI;
        const phi   = Math.acos(2 * Math.random() - 1);
        const r = radius * Math.cbrt(minR + (1 - minR) * Math.random());
        arr[i*3]   = r * Math.sin(phi) * Math.cos(theta);
        arr[i*3+1] = r * Math.sin(phi) * Math.sin(theta);
        arr[i*3+2] = r * Math.cos(phi);
    }
    return arr;
}
function buildSet(texture, size) {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(makeSpherePositions(COUNT, FIELD_R), 3));
    const mat = new THREE.PointsMaterial({
        size, sizeAttenuation: true, map: texture,
        alphaTest: 0.5, transparent: true, depthWrite: false,
    });
    return new THREE.Points(geo, mat);
}
const fieldGroup = new THREE.Group();
scene.add(fieldGroup);
const pointsNo = buildSet(particleTexture1, 0.55);
const pointsX  = buildSet(particleTexture2, 0.55);
fieldGroup.add(pointsNo);
fieldGroup.add(pointsX);

/* ─────────────────────────────────────────────────────────────
   Camera / renderer
   ───────────────────────────────────────────────────────────── */
const sizes = { width: window.innerWidth, height: window.innerHeight };

const camera = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 0.1, 100);
camera.position.z = 3;
scene.add(camera);

const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
renderer.setClearColor(0x000000, 0);
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

window.addEventListener('resize', () => {
    sizes.width = window.innerWidth;
    sizes.height = window.innerHeight;
    camera.aspect = sizes.width / sizes.height;
    camera.updateProjectionMatrix();
    renderer.setSize(sizes.width, sizes.height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
});

/* Mouse → tilt target */
const mouseN = { x: 0, y: 0 };
const targetTilt  = { x: 0, y: 0 };
const currentTilt = { x: 0, y: 0 };
window.addEventListener('pointermove', (e) => {
    mouseN.x =  (e.clientX / sizes.width)  * 2 - 1;
    mouseN.y = -(e.clientY / sizes.height) * 2 + 1;
    const cx = Math.max(-1, Math.min(1, mouseN.x));
    const cy = Math.max(-1, Math.min(1, mouseN.y));
    targetTilt.y =  cx * MAX_TILT;
    targetTilt.x = -cy * MAX_TILT;
});

const tick = () => {
    currentTilt.x += (targetTilt.x - currentTilt.x) * TILT_LERP;
    currentTilt.y += (targetTilt.y - currentTilt.y) * TILT_LERP;
    fieldGroup.rotation.x = currentTilt.x;
    fieldGroup.rotation.y = currentTilt.y;
    pointsNo.rotation.z += 0.0004;
    pointsX.rotation.z  -= 0.0004;
    renderer.render(scene, camera);
    window.requestAnimationFrame(tick);
};
tick();

/* ─────────────────────────────────────────────────────────────
   ANALOG CLOCK
   ───────────────────────────────────────────────────────────── */
function updateClock() {
    const s2 = document.querySelector('.second-hand');
    const m2 = document.querySelector('.minute-hand');
    const h2 = document.querySelector('.hour-hand');
    if (!s2 || !m2 || !h2) return;
    const now = new Date();
    const s = now.getSeconds() + now.getMilliseconds() / 1000;
    const m = now.getMinutes() + s / 60;
    const h = (now.getHours() % 12) + m / 60;
    s2.style.transform = `rotate(${s * 6}deg)`;
    m2.style.transform = `rotate(${m * 6}deg)`;
    h2.style.transform = `rotate(${h * 30}deg)`;
}
setInterval(updateClock, 1000);
updateClock();

/* ─────────────────────────────────────────────────────────────
   SUBJECT LIFT
   Recreates Apple's VisionKit Subject Lift animated outline:
   1. Load the logo PNG into an offscreen canvas at a manageable
      resolution.
   2. Read the alpha channel into a 2D grid.
   3. Run marching-squares (isoBands) at alpha threshold ≈ 0.5 to
      get the true silhouette as a list of closed polygons.
   4. Keep the largest polygon (the logo body); simplify slightly
      to reduce path length.
   5. Convert to an SVG <path>, positioned to align exactly with
      the hero logo's on-screen rect.
   6. Render two overlapping paths in the overlay SVG:
        a) a soft continuous glow (low-opacity red)
        b) a bright shimmer dash that walks around the outline via
           animated stroke-dashoffset, with a white core.
   The bloom filter and mix-blend-mode:screen give it that
   "traced by light" quality that reads as ML discovery.
   ───────────────────────────────────────────────────────────── */

// Ramer–Douglas–Peucker path simplification — reduces vertex count
// while preserving overall shape. Cuts our path from ~1200 pts to ~250.
function rdp(points, epsilon) {
    if (points.length < 3) return points.slice();
    let maxD = 0, idx = 0;
    const [ax, ay] = points[0], [bx, by] = points[points.length - 1];
    const dx = bx - ax, dy = by - ay;
    const lineLen2 = dx * dx + dy * dy || 1;
    for (let i = 1; i < points.length - 1; i++) {
        const [x, y] = points[i];
        const t = ((x - ax) * dx + (y - ay) * dy) / lineLen2;
        const tc = Math.max(0, Math.min(1, t));
        const px = ax + tc * dx, py = ay + tc * dy;
        const d = Math.hypot(x - px, y - py);
        if (d > maxD) { maxD = d; idx = i; }
    }
    if (maxD > epsilon) {
        const left  = rdp(points.slice(0, idx + 1), epsilon);
        const right = rdp(points.slice(idx),        epsilon);
        return left.slice(0, -1).concat(right);
    }
    return [points[0], points[points.length - 1]];
}

function polygonToSubpathD(pts) {
    if (!pts.length) return '';
    let d = `M ${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)}`;
    for (let i = 1; i < pts.length; i++) {
        d += ` L ${pts[i][0].toFixed(1)} ${pts[i][1].toFixed(1)}`;
    }
    d += ' Z';
    return d;
}

function polygonPerimeter(pts) {
    let p = 0;
    for (let i = 0; i < pts.length - 1; i++) {
        p += Math.hypot(pts[i+1][0] - pts[i][0], pts[i+1][1] - pts[i][1]);
    }
    return p;
}

function polygonAbsArea(pts) {
    let a = 0;
    for (let j = 0; j < pts.length - 1; j++) {
        a += pts[j][0] * pts[j+1][1] - pts[j+1][0] * pts[j][1];
    }
    return Math.abs(a) / 2;
}

async function extractLogoOutline() {
    // Load the actual image
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = logoUrl;
    await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });

    // Downsample into a workable resolution for marching-squares (speed vs. fidelity)
    const targetMax = 320;
    const scale = Math.min(1, targetMax / Math.max(img.naturalWidth, img.naturalHeight));
    const w = Math.max(2, Math.round(img.naturalWidth  * scale));
    const h = Math.max(2, Math.round(img.naturalHeight * scale));

    const off = document.createElement('canvas');
    off.width = w; off.height = h;
    const ctx = off.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, w, h);
    const { data } = ctx.getImageData(0, 0, w, h);

    // Alpha grid, row-major [y][x], values 0..255
    const grid = new Array(h);
    for (let y = 0; y < h; y++) {
        const row = new Array(w);
        for (let x = 0; x < w; x++) row[x] = data[(y * w + x) * 4 + 3];
        grid[y] = row;
    }

    // isoBands returns polygons for the alpha band 128..256 (the "solid" alpha).
    // For a subject like the Antiultra logotype this is legitimately MANY
    // disjoint polygons: two big letterform islands + several small counters.
    // The fundamental fix is: do NOT cherry-pick one — render every polygon
    // above a meaningful area threshold as subpaths of a single compound path.
    // This is topology-agnostic; any future logo (one blob, ten blobs, holes)
    // will trace correctly with no code changes.
    const bands = isoBands(grid, [128], [128]);
    if (!bands || !bands[0]) return { pathD: '', perimeter: 0, srcW: w, srcH: h };

    // Score all polygons, drop degenerate (<4 pts) and near-zero-area noise.
    const scored = [];
    for (const poly of bands[0]) {
        if (!poly || poly.length < 4) continue;
        const area = polygonAbsArea(poly);
        if (area < 1) continue; // sub-pixel noise
        scored.push({ poly, area });
    }
    if (!scored.length) return { pathD: '', perimeter: 0, srcW: w, srcH: h };

    // Drop micro-polygons below 0.5% of the largest polygon's area. Keeps
    // meaningful glyph counters, drops speckle. Same threshold works for
    // any subject: if the biggest island is huge, a 0.5% counter is still
    // visible on screen; if everything is tiny, the ratio still holds.
    const maxArea = Math.max(...scored.map(s => s.area));
    const keep = scored.filter(s => s.area >= maxArea * 0.005);

    // Simplify each subpath, then concatenate into a single compound path.
    // Total perimeter is the sum, so the shimmer dash calibrates to walking
    // the WHOLE silhouette (all subpaths in sequence).
    let compoundD = '';
    let totalPerimeter = 0;
    for (const { poly } of keep) {
        const simplified = rdp(poly, 0.9);
        if (simplified.length < 3) continue;
        compoundD += (compoundD ? ' ' : '') + polygonToSubpathD(simplified);
        totalPerimeter += polygonPerimeter(simplified);
    }

    return { pathD: compoundD, perimeter: totalPerimeter, srcW: w, srcH: h };
}

let subjectPathEl = null;
let subjectGlowEl = null;
let shimmerAnimHandle = 0;

async function initSubjectLift() {
    const svg   = document.getElementById('subjectlift');
    const group = document.getElementById('subjectliftGroup');
    const logo  = document.getElementById('heroLogo');
    if (!svg || !group || !logo) return;

    // Wait for the logo <img> to have real dimensions on screen
    if (!logo.complete) await new Promise(res => logo.addEventListener('load', res, { once: true }));

    const outline = await extractLogoOutline();
    if (!outline.pathD) return;

    // Position the outline so it aligns with the on-screen logo rect
    const layout = () => {
        const r = logo.getBoundingClientRect();
        const sx = r.width  / outline.srcW;
        const sy = r.height / outline.srcH;
        // Wrap the raw path in a group transformed to logo screen coords
        const wrapper = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        wrapper.setAttribute('transform', `translate(${r.left.toFixed(2)}, ${r.top.toFixed(2)}) scale(${sx.toFixed(4)}, ${sy.toFixed(4)})`);

        // Faint continuous glow — establishes the whole outline exists
        const glow = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        glow.setAttribute('d', outline.pathD);
        glow.setAttribute('fill', 'none');
        glow.setAttribute('stroke', 'rgba(255, 30, 30, 0.35)');
        glow.setAttribute('stroke-width', String(2 / Math.min(sx, sy)));
        glow.setAttribute('stroke-linejoin', 'round');
        glow.setAttribute('stroke-linecap',  'round');
        wrapper.appendChild(glow);

        // Bright shimmer dash — walks around the outline
        const shimmer = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        shimmer.setAttribute('d', outline.pathD);
        shimmer.setAttribute('fill', 'none');
        shimmer.setAttribute('stroke', 'white');
        shimmer.setAttribute('stroke-width', String(3.2 / Math.min(sx, sy)));
        shimmer.setAttribute('stroke-linejoin', 'round');
        shimmer.setAttribute('stroke-linecap',  'round');

        // Dash of length = ~14% of perimeter, with the rest transparent,
        // then animate offset from 0 → -perimeter to walk it around.
        const P = outline.perimeter;
        const dashLen = Math.max(30, P * 0.14);
        const gapLen  = P;
        shimmer.setAttribute('stroke-dasharray', `${dashLen} ${gapLen}`);
        wrapper.appendChild(shimmer);

        // Reset group and inject
        group.innerHTML = '';
        group.appendChild(wrapper);

        subjectGlowEl = glow;
        subjectPathEl = shimmer;

        // Animate stroke-dashoffset in JS so we can control speed & re-trigger easily
        if (shimmerAnimHandle) cancelAnimationFrame(shimmerAnimHandle);
        const start = performance.now();
        const cycleMs = 2400; // one full walk around the outline every 2.4s
        const step = (now) => {
            const t = ((now - start) % cycleMs) / cycleMs; // 0..1
            const offset = -t * (dashLen + gapLen);
            shimmer.setAttribute('stroke-dashoffset', offset.toFixed(1));
            // Subtle breathing of the faint glow layer for extra "alive" feel
            const breath = 0.28 + Math.sin((now - start) / 800) * 0.10;
            glow.setAttribute('stroke', `rgba(255, 30, 30, ${breath.toFixed(3)})`);
            shimmerAnimHandle = requestAnimationFrame(step);
        };
        shimmerAnimHandle = requestAnimationFrame(step);
    };

    layout();
    window.addEventListener('resize', layout);

    // Fade the SVG in once ready (avoids the pre-layout flicker)
    requestAnimationFrame(() => svg.classList.add('is-ready'));
}
initSubjectLift();

function stopSubjectLift() {
    if (shimmerAnimHandle) cancelAnimationFrame(shimmerAnimHandle);
    shimmerAnimHandle = 0;
}

/* ─────────────────────────────────────────────────────────────
   LIGHTSWITCH INTRO
   ───────────────────────────────────────────────────────────── */
const zapAudio = new Audio(zapMp3Url);
zapAudio.preload = 'auto';
zapAudio.volume = 0.75;

function playZap() {
    try {
        zapAudio.currentTime = 0;
        const p = zapAudio.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
    } catch (_) {}
}

function initLightswitch() {
    const overlay = document.getElementById('lightswitch');
    const dot     = document.getElementById('lightswitchDot');
    const flash   = document.getElementById('lightswitchFlash');
    if (!overlay || !dot || !flash) return;

    const trigger = () => {
        playZap();
        flash.classList.add('is-flashing');
        setTimeout(() => {
            stopSubjectLift();
            overlay.classList.add('is-hiding');
        }, 520);
        setTimeout(() => overlay.remove(), 1800);
    };
    dot.addEventListener('click', trigger, { once: true });
    dot.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); trigger(); }
    });
}
initLightswitch();
